<template>
  <div
    v-if="isLoading"
    class="relative flex items-center justify-center"
  >
    Loading Receipt
    <IconLoading
      class="ml-2 size-6 animate-spin text-primary"
    />
  </div>

  <div
    v-else-if="isError"
    class="flex items-center justify-center space-x-2"
  >
    <ErrorText> Failed to load receipt </ErrorText>

    <button
      class="flex items-center text-primary"
      @click="() => refetch()"
    >
      Retry
      <IconRefresh class="ml-1 size-4" />
    </button>
  </div>

  <HeadlessDisclosure
    v-else-if="receiptString"
    #default="{ open: receiptIsOpen }"
  >
    <Transition
      enter-active-class="transition-all duration-300 overflow-hidden"
      enter-from-class="transform scale-95 opacity-0 max-h-0"
      enter-to-class="transform scale-100 opacity-100 max-h-[1000px]"
      leave-active-class="transition-all duration-200 overflow-hidden"
      leave-from-class="transform max-h-[1000px]"
      leave-to-class="transform max-h-0"
    >
      <HeadlessDisclosurePanel>
        <div class="flex flex-col items-center pb-4 pt-6">
          <div class="w-full max-w-xs space-y-2">
            <ReceiptPreview
              :receipt-string="receiptString"
            />
            <div class="flex flex-col space-y-2">
              <FormSubmitButton
                type="button"
                class="col-span-2"
                @click="onDownloadReceipt"
              >
                Download Merchant Receipt
              </FormSubmitButton>
              <FormSubmitButton
                type="button"
                @click="emailModalIsOpen = true"
              >
                Email Receipt
              </FormSubmitButton>
              <FormSubmitButton
                v-if="organizationConfig?.smsServiceEnabled"
                type="button"
                @click="smsModalIsOpen = true"
              >
                SMS Receipt
              </FormSubmitButton>
            </div>
          </div>
        </div>
      </HeadlessDisclosurePanel>
    </Transition>
    <HeadlessDisclosureButton
      :class="[
        'flex w-full items-center justify-center rounded-lg py-2',
        'hover:bg-background-fg/3',
      ]"
    >
      <template v-if="receiptIsOpen">
        <span> Hide Receipt </span>
        <IconChevronUp class="size-6" />
      </template>
      <template v-else>
        <span> Show Receipt </span>
        <IconChevronDown class="size-6" />
      </template>
    </HeadlessDisclosureButton>
  </HeadlessDisclosure>

  <ReceiptEmailModal
    :is-open="emailModalIsOpen"
    :transaction-id="transactionId"
    @close="emailModalIsOpen = false"
  />
  <ReceiptSmsModal
    :is-open="smsModalIsOpen"
    :transaction-id="transactionId"
    @close="smsModalIsOpen = false"
  />
</template>

<script setup lang="ts">
const props = defineProps<{
  transactionId: string
}>()
const { organizationConfig } = useOrganizationConfig()

const emailModalIsOpen = ref(false)
const smsModalIsOpen = ref(false)

const {
  data: transactionReceiptData,
  isError,
  isLoading,
  refetch,
} = useTransactionReceipt(props.transactionId, {
  isMerchant: true,
})

const receiptString = computed(
  () => transactionReceiptData.value?.receipt
)

const onDownloadReceipt = () => {
  if (!receiptString.value) return

  download(
    receiptString.value,
    `Transaction receipt (merchant).txt`,
    "text/plain"
  )
}
</script>
